import { useState, useEffect, useMemo } from 'react';
import { MessageCircle, X, Clock, Globe, Info } from 'lucide-react';
import {
  convertBusinessHours,
  formatBusinessHoursShort,
  getTimezoneFriendlyName,
  isArgentinaTimezone,
  type ConvertedBusinessHours
} from '../utils/timezoneConverter';

export default function WhatsAppButton() {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [showTimezoneInfo, setShowTimezoneInfo] = useState(false);
  const [convertedHours, setConvertedHours] = useState<ConvertedBusinessHours | null>(null);

  useEffect(() => {
    const hours = convertBusinessHours();
    setConvertedHours(hours);
  }, []);

  const isWithinBusinessHours = (): boolean => {
    const argentinaTime = new Date().toLocaleString('en-US', {
      timeZone: 'America/Argentina/Buenos_Aires'
    });
    const now = new Date(argentinaTime);

    const day = now.getDay();
    const hour = now.getHours();

    const isWeekday = day >= 1 && day <= 5;
    const isBusinessHour = hour >= 9 && hour < 19;

    return isWeekday && isBusinessHour;
  };

  const getWhatsAppMessage = (): string => {
    if (isWithinBusinessHours()) {
      return "Hola, quiero convertirme en referente del trabajo con datos en mi trabajo, ¿por dónde empiezo?";
    } else {
      return "Hola, estoy interesado/a en el programa. Entiendo que están fuera del horario de atención (lunes a viernes de 9 a 19 hs), pero me gustaría dejar mi consulta. ¡Gracias!";
    }
  };

  const getPopupMessage = (): string => {
    if (isWithinBusinessHours()) {
      return "¿Tenés dudas sobre el programa? ¡Escribime por WhatsApp y te ayudo a encontrar el mejor camino para vos!";
    } else {
      return "¡Escribinos igual y te responderemos cuando volvamos!";
    }
  };

  const businessHoursDisplay = useMemo(() => {
    if (!convertedHours) return 'Lunes a viernes 9:00 AM - 7:00 PM (hora Argentina)';

    const isArgentina = isArgentinaTimezone(convertedHours.visitorTimezone);
    const hoursText = formatBusinessHoursShort(convertedHours);

    if (isArgentina) {
      return `Lunes a viernes ${hoursText}`;
    }

    return `Lunes a viernes ${hoursText}`;
  }, [convertedHours]);

  const timezoneLocationDisplay = useMemo(() => {
    if (!convertedHours) return 'Argentina';
    if (isArgentinaTimezone(convertedHours.visitorTimezone)) {
      return 'Argentina';
    }
    return getTimezoneFriendlyName(convertedHours.visitorTimezone);
  }, [convertedHours]);

  const getStatusIndicator = () => {
    if (isWithinBusinessHours()) {
      return (
        <p className="text-xs text-green-600 flex items-center gap-1">
          <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
          En línea
        </p>
      );
    } else {
      return (
        <p className="text-xs text-amber-600 flex items-center gap-1">
          <Clock className="w-3 h-3" />
          Fuera de horario
        </p>
      );
    }
  };

  const whatsappUrl = `https://wa.me/5491153895098?text=${encodeURIComponent(getWhatsAppMessage())}`;

  const handleClick = () => {
    window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
  };

  return (
    <>
      <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end gap-3">
        {isExpanded && (
          <div className="animate-fadeIn bg-white rounded-2xl shadow-2xl border-2 border-green-500/20 p-4 max-w-xs mb-2">
            <div className="flex items-start justify-between mb-2">
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center">
                  <MessageCircle className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h4 className="font-bold text-neutral-900 text-sm">Alejandro Romanisio</h4>
                  {getStatusIndicator()}
                </div>
              </div>
              <button
                onClick={() => setIsExpanded(false)}
                className="text-neutral-400 hover:text-neutral-600 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <p className="text-sm text-neutral-700 mb-3 leading-relaxed">
              {getPopupMessage()}
            </p>

            <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-3 relative">
              <div className="flex items-start gap-2">
                <Clock className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-xs font-semibold text-green-800 mb-1">Horario de atención</p>
                  <p className="text-sm text-green-700 font-medium">
                    {businessHoursDisplay}
                  </p>
                  {convertedHours && !isArgentinaTimezone(convertedHours.visitorTimezone) && (
                    <div className="mt-2 pt-2 border-t border-green-200">
                      <p className="text-xs text-green-600 flex items-center gap-1">
                        <Globe className="w-3 h-3" />
                        <span>Tu zona horaria: {timezoneLocationDisplay}</span>
                      </p>
                    </div>
                  )}
                </div>
                <button
                  onMouseEnter={() => setShowTimezoneInfo(true)}
                  onMouseLeave={() => setShowTimezoneInfo(false)}
                  onClick={() => setShowTimezoneInfo(!showTimezoneInfo)}
                  className="text-green-600 hover:text-green-800 transition-colors flex-shrink-0"
                  aria-label="¿Por qué veo estos horarios?"
                >
                  <Info className="w-4 h-4" />
                </button>
              </div>

              {showTimezoneInfo && (
                <div className="mt-2 pt-2 border-t border-green-200 animate-fadeIn">
                  <p className="text-xs text-green-700 leading-relaxed">
                    {convertedHours && !isArgentinaTimezone(convertedHours.visitorTimezone) ? (
                      <>
                        Estos horarios se muestran en <strong>tu zona horaria local</strong> por conveniencia.
                        Nuestra oficina opera de <strong>9:00 AM a 7:00 PM hora Argentina</strong> (lunes a viernes).
                      </>
                    ) : (
                      <>
                        Estamos disponibles de <strong>lunes a viernes</strong> en estos horarios.
                        ¡Escribinos cuando quieras!
                      </>
                    )}
                  </p>
                </div>
              )}
            </div>

            <button
              onClick={handleClick}
              className="w-full bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-bold py-2.5 px-4 rounded-xl transition-all duration-300 hover:scale-[1.02] shadow-lg text-sm flex items-center justify-center gap-2"
            >
              <MessageCircle className="w-4 h-4" />
              Iniciar conversación
            </button>
          </div>
        )}

        <button
          onClick={() => setIsExpanded(!isExpanded)}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          className="group relative w-16 h-16 bg-gradient-to-br from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 rounded-full shadow-2xl hover:shadow-green-500/50 transition-all duration-300 hover:scale-110 flex items-center justify-center"
          aria-label="Abrir chat de WhatsApp"
        >
          <MessageCircle className="w-8 h-8 text-white transition-transform duration-300 group-hover:scale-110" />

          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full border-2 border-white flex items-center justify-center">
            <span className="w-2 h-2 bg-white rounded-full animate-pulse"></span>
          </span>

          {isHovered && !isExpanded && (
            <div className="absolute right-full mr-4 top-1/2 -translate-y-1/2 whitespace-nowrap bg-neutral-900 text-white px-4 py-2 rounded-lg text-sm font-semibold shadow-xl animate-fadeIn">
              ¿Tenés dudas? ¡Consultanos!
              <div className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-full">
                <div className="w-0 h-0 border-t-8 border-b-8 border-l-8 border-transparent border-l-neutral-900"></div>
              </div>
            </div>
          )}
        </button>
      </div>

      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
      `}</style>
    </>
  );
}
